import { renderToString } from 'react-dom/server';

export const IconUtil = {
  getBitmap: async (element: React.ReactNode, size: number, frame: 'circle' | 'square' | 'none' = 'circle') => {
    let svgString = renderToString(element);
    const padding = 4;
    const svgWidth = size + padding * 2;
    const svgHeight = size + padding * 2;

    if (frame === 'circle') {
      svgString = `
        <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg">
          <!-- Circle for the border -->
          <circle cx="${svgWidth / 2}" cy="${svgHeight / 2}" r="${svgWidth / 2 - 2}" stroke="#ccc" stroke-width="1" fill="white" />
          <!-- Center the icon SVG -->
          <g transform="translate(${padding}, ${padding})">
            ${svgString}
          </g>
        </svg>
        `;
    } else if (frame === 'square') {
      svgString = `
        <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg">
          <!-- Square for the border -->
          <rect x="0" y="0" rx="9" ry="9" width="${svgWidth}" height="${svgHeight}" stroke="#ccc" stroke-width="1" fill="white" />
          <!-- Center the icon SVG -->
          <g transform="translate(${padding}, ${padding})">
            ${svgString}
          </g>
        </svg>
        `;
    } else {
      svgString = `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          ${svgString}
        </svg>
        `;
    }
    const encodedSvg = encodeURIComponent(svgString.trim());
    const dataUrl = `data:image/svg+xml,${encodedSvg}`;


    // Step 3: Create an ImageBitmap
    async function createImageBitmapFromSvg() {
      try {
        // Load the SVG as an image
        const img = new Image();
        img.src = dataUrl;

        // Wait for the image to load
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Create the ImageBitmap
        const bitmap = await createImageBitmap(img);
        return bitmap;
      } catch (error) {
        console.error('Error creating ImageBitmap:', error);
        return null;
      }
    }
    return createImageBitmapFromSvg();
  },

  getTextAvatarBitmap: (text: string, bgColor: string, fgColor: string = 'white') => {
    // Take first 2 characters and make them uppercase
    const displayText = text.slice(0, 2).toUpperCase();
    const size = 40; // Default size
    const padding = 2; // Padding around the circle
    const svgWidth = size;
    const svgHeight = size;

    // Create SVG with circle and text
    const svgString = `
      <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" 
           xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <!-- Background circle with border -->
        <circle cx="${svgWidth / 2}" cy="${svgHeight / 2}" 
                r="${svgWidth / 2 - padding}" 
                fill="${bgColor}" 
                stroke="white" 
                stroke-width="2" />
        <!-- Text -->
        <text x="50%" y="52%"
              font-family="Arial, sans-serif"
              font-size="${size * 0.4}"
              fill="${fgColor}"
              text-anchor="middle"
              dominant-baseline="central">
          ${displayText}
        </text>
      </svg>
    `;

    const encodedSvg = encodeURIComponent(svgString.trim());
    const dataUrl = `data:image/svg+xml,${encodedSvg}`;

    // Create and return ImageBitmap
    async function createImageBitmapFromSvg() {
      try {
        const img = new Image();
        img.src = dataUrl;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const bitmap = await createImageBitmap(img);
        return bitmap;
      } catch (error) {
        console.error('Error creating text avatar ImageBitmap:', error);
        return null;
      }
    }

    return createImageBitmapFromSvg();
  },

  getCommentAvatarBitmap: (text: string, bgColor: string, fgColor: string = 'white') => {
    // Take first 2 characters and make them uppercase
    const displayText = text.slice(0, 2).toUpperCase();
    const size = 32; // Smaller size for comments
    const padding = 2; // Padding around the circle
    const svgWidth = size;
    const svgHeight = size;
    const triangleSize = 14; // Even larger triangle for prominent pointer effect

    // Create SVG with triangle behind circle
    const svgString = `
      <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}"
           xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <!-- Triangle behind circle (drawn first so it's behind) -->
        <polygon points="${svgWidth / 2},${svgHeight - padding} ${svgWidth / 2 - triangleSize / 2},${svgHeight - padding - triangleSize} ${svgWidth / 2 + triangleSize / 2},${svgHeight - padding - triangleSize}"
                 fill="white" />
        <!-- Background circle with border (drawn on top of triangle) -->
        <circle cx="${svgWidth / 2}" cy="${svgHeight / 2}"
                r="${svgWidth / 2 - padding}"
                fill="${bgColor}"
                stroke="white"
                stroke-width="2" />
        <!-- Text -->
        <text x="50%" y="52%"
              font-family="Arial, sans-serif"
              font-size="${size * 0.32}"
              fill="${fgColor}"
              text-anchor="middle"
              dominant-baseline="central">
          ${displayText}
        </text>
      </svg>
    `;

    const encodedSvg = encodeURIComponent(svgString.trim());
    const dataUrl = `data:image/svg+xml,${encodedSvg}`;

    // Create and return ImageBitmap
    async function createImageBitmapFromSvg() {
      try {
        const img = new Image();
        img.src = dataUrl;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const bitmap = await createImageBitmap(img);
        return bitmap;
      } catch (error) {
        console.error('Error creating comment avatar ImageBitmap:', error);
        return null;
      }
    }

    return createImageBitmapFromSvg();
  },
}